import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  OutlinedInput,
  Paper,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import promoLabels from "../../public/locales/en/promoCodes.json";
import { adminApiClient } from "../services/apiClient";

interface PromoCode {
  planName: "Premium" | "Pro";
  id: string;
  code: string;
  description: string | null;
  durationMonths: number;
  maxActivations: number;
  usedCount: number;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  _count: { activations: number };
}

interface PromoCodeActivation {
  id: string;
  activatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}

interface PromoCodeDetail extends PromoCode {
  activations: PromoCodeActivation[];
}

interface CreateForm {
  planName: "Premium" | "Pro";
  code: string;
  description: string;
  durationMonths: string;
  maxActivations: string;
  expiresAt: string;
}

const emptyForm: CreateForm = {
  planName: "Pro",
  code: "",
  description: "",
  durationMonths: "60",
  maxActivations: "1",
  expiresAt: "",
};

const PromoCodesPage: React.FC = () => {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState<PromoCodeDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success",
  ): void => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCodes = useCallback(async (): Promise<void> => {
    try {
      const res = await adminApiClient.get<{ promoCodes: PromoCode[] }>(
        "/admin/promo-codes",
      );
      setCodes(res.data.promoCodes);
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to load promo codes",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect((): void => {
    void fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async (): Promise<void> => {
    if (!form.code || !form.durationMonths || !form.maxActivations) return;
    setSubmitting(true);
    try {
      await adminApiClient.post("/admin/promo-codes", {
        code: form.code.toUpperCase(),
        planName: form.planName,
        description: form.description || undefined,
        durationMonths: Number(form.durationMonths),
        maxActivations: Number(form.maxActivations),
        expiresAt: form.expiresAt || undefined,
      });
      showSnackbar("Promo code created");
      setCreateOpen(false);
      setForm(emptyForm);
      await fetchCodes();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create promo code";
      showSnackbar(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id: string, code: string): Promise<void> => {
    if (!confirm(`Deactivate promo code "${code}"?`)) return;
    try {
      await adminApiClient.patch(`/admin/promo-codes/${id}`, {
        isActive: false,
      });
      showSnackbar("Promo code deactivated");
      await fetchCodes();
    } catch {
      showSnackbar("Failed to deactivate", "error");
    }
  };

  const handleViewDetail = async (id: string): Promise<void> => {
    try {
      const res = await adminApiClient.get<{ promoCode: PromoCodeDetail }>(
        `/admin/promo-codes/${id}`,
      );
      setDetail(res.data.promoCode);
      setDetailOpen(true);
    } catch {
      showSnackbar("Failed to load details", "error");
    }
  };

  const copyToClipboard = (text: string): void => {
    void navigator.clipboard.writeText(text);
    showSnackbar("Copied to clipboard");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight={600}>
          Promo Codes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Promo Code
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Duration</TableCell>
              <TableCell align="center">Activations</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {codes.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  No promo codes yet
                </TableCell>
              </TableRow>
            )}
            {codes.map((c) => (
              <TableRow key={c.id} sx={{ opacity: c.isActive ? 1 : 0.5 }}>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight={600}
                    >
                      {c.code}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(c.code)}
                      >
                        <CopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {c.description ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{c.durationMonths} mo</Typography>
                  <Chip
                    size="small"
                    label={
                      c.planName === "Premium"
                        ? promoLabels.premium
                        : promoLabels.pro
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {c.usedCount} /{" "}
                    {c.maxActivations === 0 ? "∞" : c.maxActivations}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {c.expiresAt
                      ? new Date(c.expiresAt).toLocaleDateString()
                      : "Never"}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={c.isActive ? "Active" : "Inactive"}
                    color={c.isActive ? "success" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" justifyContent="center" gap={0.5}>
                    <Tooltip title="View activations">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetail(c.id)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {c.isActive && (
                      <Tooltip title="Deactivate">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeactivate(c.id, c.code)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Promo Code</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              select
              label={promoLabels.plan}
              value={form.planName}
              onChange={(event): void =>
                setForm({
                  ...form,
                  planName: event.target.value as "Premium" | "Pro",
                })
              }
            >
              <MenuItem value="Premium">{promoLabels.premium}</MenuItem>
              <MenuItem value="Pro">{promoLabels.pro}</MenuItem>
            </TextField>
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              inputProps={{
                style: { fontFamily: "monospace", textTransform: "uppercase" },
              }}
              helperText="Will be saved in uppercase"
              required
            />
            <TextField
              label="Description (internal note)"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="e.g. For @shadowing_tips influencer"
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Duration (months)"
                  type="number"
                  value={form.durationMonths}
                  onChange={(e) =>
                    setForm({ ...form, durationMonths: e.target.value })
                  }
                  inputProps={{ min: 1 }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Max activations (0 = unlimited)"
                  type="number"
                  value={form.maxActivations}
                  onChange={(e) =>
                    setForm({ ...form, maxActivations: e.target.value })
                  }
                  inputProps={{ min: 0 }}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel shrink>Expires at (optional)</InputLabel>
              <OutlinedInput
                notched
                label="Expires at (optional)"
                type="date"
                value={form.expiresAt}
                onChange={(e) =>
                  setForm({ ...form, expiresAt: e.target.value })
                }
              />
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontFamily="monospace" fontWeight={700} fontSize={18}>
              {detail?.code}
            </Typography>
            <Chip
              size="small"
              label={`${detail?.usedCount} / ${detail?.maxActivations === 0 ? "∞" : detail?.maxActivations} activations`}
            />
          </Stack>
        </DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={500}>Info</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={1}>
                    <Typography variant="body2">
                      <strong>Description:</strong> {detail.description ?? "—"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Duration:</strong> {detail.durationMonths} months
                    </Typography>
                    <Typography variant="body2">
                      <strong>Expires:</strong>{" "}
                      {detail.expiresAt
                        ? new Date(detail.expiresAt).toLocaleDateString()
                        : "Never"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong>{" "}
                      {new Date(detail.createdAt).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" mb={1} mt={1}>
                Activations ({detail.activations.length})
              </Typography>
              {detail.activations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No activations yet
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Activated At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.activations.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>
                            {[a.user.firstName, a.user.lastName]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </TableCell>
                          <TableCell>{a.user.email}</TableCell>
                          <TableCell>
                            {new Date(a.activatedAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromoCodesPage;
